import { PropsWithChildren, useEffect, useState } from 'react';

import { useSessionStorageState } from 'shared/hooks';

import {
  DEFAULT_SEARCH_CONTEXT_ZIP_FILTER,
  type SearchContextValue,
  type SearchContextZipFilter,
} from '../types';
import { SearchContext, DEFAULT_CONTEXT_VALUE } from './SearchContext';

export interface SearchContextProps<T, D> {
  defaultSortOption?: SearchContextValue<T>['sortOption'];
  /** @default true */
  defaultDisplayList?: boolean;
  defaultFilterOption?: SearchContextValue<T>['filter'];

  /**
   * Default date filter.
   * @default null
   */
  defaultDateFilter?: D;

  /** Allow to save the state of the `displayList` value in local storage. Left empty to DO NOT persist. */
  displayListPersistence?: string | false;

  searchQuery?: string;

  /** Default ZIP filter. */
  zipFilter?: SearchContextZipFilter;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function SearchProvider<T, D = any>(props: PropsWithChildren<SearchContextProps<T, D>>) {
  const [isUserTyping, setIsUserTyping] = useState(false);

  const [sortOption, setSortOption] = useState(
    props.defaultSortOption ?? DEFAULT_CONTEXT_VALUE.sortOption,
  );

  const [searchQuery, setSearchQuery] = useState<string>(props.searchQuery ?? '');
  const [filter, setFilter] = useState<T>(props.defaultFilterOption as T);
  const [dateFilter, setDateFilter] = useState(props.defaultDateFilter);
  const [zipQuery, setZipQuery] = useState('');
  const [zipFilter, setZipFilter] = useState(props.zipFilter ?? DEFAULT_SEARCH_CONTEXT_ZIP_FILTER);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);

  const [displayList, setDisplayList] = useSessionStorageState(
    props.displayListPersistence || undefined,
    props.defaultDisplayList,
  );

  useEffect(() => {
    if (typeof props.searchQuery === 'string') {
      setSearchQuery(props.searchQuery);
    }
  }, [props.searchQuery]);

  const openFilterModal = () => setIsFilterModalOpen(true);

  const closeFilterModal = () => setIsFilterModalOpen(false);

  return (
    <SearchContext.Provider
      value={{
        searchQuery,
        setSearchQuery,
        isUserTyping,
        setIsUserTyping,
        displayList: displayList ?? props.defaultDisplayList ?? false,
        sortOption,
        setSortOption,
        setDisplayList,
        filter,
        setFilter,
        dateFilter,
        setDateFilter,
        zipQuery,
        setZipQuery,
        zipFilter,
        setZipFilter,
        isFilterModalOpen,
        setIsFilterModalOpen,
        openFilterModal,
        closeFilterModal,
      }}
    >
      {props.children}
    </SearchContext.Provider>
  );
}
